import { timer } from 'rxjs';
import { switchMap, catchError, startWith, tap, distinctUntilChanged } from 'rxjs/operators';
import {
    withComponent,
    sendAggregation,
    connectToState,
    createRejectedStatus,
    loadingStatus,
    withStore,
} from '@pendo/agg-connect';
import { LOADING_STATES } from '@/constants';

/**
 * Creates a standard time series configuration for hover card metrics.
 * Defaults to last 30 days.
 *
 * @returns {Object} Time series configuration
 */
export function createMetricsTimeSeries () {
    return {
        period: 'dayRange',
        first: 'dateAdd(now(), -30, "days")',
        count: 30,
    };
}

/**
 * Creates an RxJS observable for fetching hover card metrics.
 *
 * @param {Object} options - Configuration options
 * @param {Object|Function} options.aggregation - The aggregation to execute, or a function that returns it based on params
 * @param {string} options.streamId - Unique stream identifier
 * @param {Object} options.componentParams - Component properties to watch (e.g., { appId: 'appId' })
 * @param {Object} options.component - Vue component instance
 * @param {Object} options.store - Vuex store instance
 * @param {Function} options.onAggStart - Callback when aggregation starts
 * @returns {Observable} RxJS observable that emits aggregation status updates
 */
export function createMetricsObservable ({
    aggregation,
    streamId,
    componentParams,
    component,
    store,
    onAggStart,
}) {
    const timeSeries = createMetricsTimeSeries();

    const state$ = connectToState(
        withComponent(componentParams),
        withStore({
            includeAnonymous: (state, getters) => getters['globalFilters/segmentIncludeAnonymous'],
        }),
    )({ component, store });

    return state$.pipe(
        switchMap((params) => {
            // Allow aggregation to be a function that selects based on params (e.g., type)
            const selectedAggregation = typeof aggregation === 'function'
                ? aggregation(params)
                : aggregation;

            return timer(500).pipe(
                tap(() => {
                    if (onAggStart) {
                        onAggStart();
                    }
                }),
                switchMap(() => sendAggregation(
                    selectedAggregation,
                    streamId,
                    { ...params, timeSeries },
                )),
                startWith(loadingStatus),
            );
        }),
        catchError((error, caught) => {
            return caught.pipe(startWith(createRejectedStatus(error)));
        }),
        distinctUntilChanged(),
    );
}

/**
 * Handles metrics response and returns state updates.
 *
 * @param {Object} response - Response from aggregation
 * @param {string} response.status - Status of the aggregation (LOADING, RESOLVED, REJECTED)
 * @param {Object} response.value - Aggregation result values
 * @returns {Object} State updates to apply to component
 */
export function handleMetricsResponse ({ status, value }) {
    const { LOADING, RESOLVED, REJECTED } = LOADING_STATES;

    switch (status) {
        case LOADING:
            return {
                isLoadingMetrics: true,
                hasMetricsError: false,
                visitorsValue: 0,
                accountsValue: 0,
                occurrencesValue: 0,
            };
        case RESOLVED:
            return {
                isLoadingMetrics: false,
                hasMetricsError: false,
                visitorsValue: value.uniqueVisitors || 0,
                accountsValue: value.uniqueAccounts || 0,
                occurrencesValue: value.totalEvents || 0,
            };
        case REJECTED:
            return {
                isLoadingMetrics: false,
                hasMetricsError: true,
                visitorsValue: 0,
                accountsValue: 0,
                occurrencesValue: 0,
            };
        default:
            return {};
    }
}

/**
 * Sends a tracking event for hover card performance.
 *
 * @param {Object} options - Tracking options
 * @param {string} options.eventType - Type of hover card (e.g., 'app', 'page', 'feature')
 * @param {number} options.aggStartTime - Timestamp when aggregation started
 * @param {string} options.pageLocation - Current route name
 */
export function sendHoverCardTrackEvent ({ eventType, aggStartTime, pageLocation }) {
    if (!aggStartTime) return;

    window.pendo.track('Hover Card', {
        pageLocation: pageLocation || 'Unknown',
        eventType,
        aggTime: Date.now() - aggStartTime,
    });
}
